import { lazy as reactLazy } from "react";

export const lazy = reactLazy;

export const omit = <T extends Record<string, any>, K extends keyof T>(
  object: T,
  keys: K[]
): Omit<T, K> => {
  const result = { ...object };
  keys.forEach((key) => {
    delete result[key];
  });
  return result;
};

export const isMediaStream = (url: any): url is MediaStream => {
  return (
    typeof window !== "undefined" &&
    window.MediaStream &&
    url instanceof window.MediaStream
  );
};

export const supportsWebKitPresentationMode = (): boolean => {
  if (typeof window === "undefined") return false;
  const video = document.createElement("video");
  return "webkitSupportsPresentationMode" in video;
};

export const randomString = (): string => {
  return Math.random().toString(36).substr(2, 9);
};

export const parseQuery = (url: string): Record<string, string> => {
  const query: Record<string, string> = {};
  const queryString = url.split("?")[1] || "";

  if (!queryString) return query;

  const manualParse = (qs: string) => {
    qs.split("&").forEach((param) => {
      const [key, value] = param.split("=");
      if (key) {
        try {
          query[decodeURIComponent(key)] = value
            ? decodeURIComponent(value.replace(/\+/g, " "))
            : "";
        } catch (e) {
          query[key] = value || "";
        }
      }
    });
  };

  if (typeof URLSearchParams !== "undefined") {
    try {
      const params = new URLSearchParams(queryString);
      params.forEach((value, key) => {
        query[key] = value;
      });
      return query;
    } catch (e) {
      manualParse(queryString);
    }
  } else {
    manualParse(queryString);
  }

  return query;
};

export const merge = <T extends Record<string, any>>(
  target: T,
  ...sources: Partial<T>[]
): T => {
  if (!sources.length) return target;
  const source = sources.shift();

  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) Object.assign(target, { [key]: {} });
        merge(target[key] as any, source[key] as any);
      } else {
        Object.assign(target, { [key]: source[key] });
      }
    }
  }

  return merge(target, ...sources);
};

const isObject = (item: any): item is Record<string, any> => {
  return item && typeof item === "object" && !Array.isArray(item);
};

export const IS_BROWSER = typeof window !== "undefined" && window.document;
export const IS_GLOBAL =
  typeof globalThis !== "undefined" &&
  globalThis.window &&
  globalThis.window.document;
export const IS_IOS =
  IS_BROWSER && /iPad|iPhone|iPod/.test(navigator.userAgent);
export const IS_SAFARI =
  IS_BROWSER && /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

export const SUPPORTS_HLS = (): boolean => {
  if (!IS_BROWSER) return false;
  const video = document.createElement("video");
  return Boolean(video.canPlayType("application/vnd.apple.mpegurl"));
};

export const SUPPORTS_DASH = (): boolean => {
  if (!IS_BROWSER) return false;
  const video = document.createElement("video");
  return Boolean(video.canPlayType("application/dash+xml"));
};
